import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  ArrowLeft, Settings, Vote, History, RefreshCw, CheckCircle2,
  XCircle, Loader2, Edit3, Zap, Shield, Calendar, Droplets,
  ChevronRight, AlertTriangle, ThumbsUp, ThumbsDown, Minus, Play
} from 'lucide-react';
import { toast } from 'sonner';
import { BACKEND_URL, API } from '@/lib/backendUrl';


const CATEGORY_ICONS = {
  gamification: Zap, marketplace: Shield, events: Calendar, faucet: Droplets,
};
const CATEGORY_COLORS = {
  gamification: 'bg-purple-500/15 text-purple-400 border-purple-500/20',
  marketplace: 'bg-blue-500/15 text-blue-400 border-blue-500/20',
  events: 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20',
  faucet: 'bg-[#F7931A]/15 text-[#F7931A] border-[#F7931A]/20',
};
const STATUS_COLORS = {
  active: 'bg-[#F7931A]/10 text-[#F7931A] border-[#F7931A]/20',
  executed: 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  cancelled: 'bg-gray-500/10 text-gray-500 border-gray-500/20',
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

/* ─── Parameter Card ─── */
function ParamCard({ param, onPropose }) {
  const CatIcon = CATEGORY_ICONS[param.category] || Settings;
  const catColor = CATEGORY_COLORS[param.category] || CATEGORY_COLORS.gamification;
  return (
    <div className="flex items-center justify-between py-3 px-4 rounded-xl bg-white/[0.02] border border-white/5 group hover:border-white/10 transition-colors"
      data-testid={`param-${param.key}`}>
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 ${catColor.split(' ')[0]}`}>
          <CatIcon className={`w-4 h-4 ${catColor.split(' ')[1]}`} />
        </div>
        <div className="min-w-0">
          <p className="text-xs font-medium text-gray-200">{param.label}</p>
          <p className="text-[10px] text-gray-600 truncate">{param.description}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 shrink-0">
        <div className="text-right">
          <p className="text-sm font-bold mono text-gray-100">{param.current_value}</p>
          {!param.is_default && (
            <p className="text-[9px] text-gray-600">Default: {param.default}</p>
          )}
        </div>
        {param.is_default ? (
          <Badge variant="outline" className="text-[8px] border-gray-700/30 text-gray-600">Default</Badge>
        ) : (
          <Badge variant="outline" className="text-[8px] border-emerald-500/30 text-emerald-400">DAO Set</Badge>
        )}
        <Button size="sm" variant="ghost" onClick={() => onPropose(param)}
          className="opacity-0 group-hover:opacity-100 text-gray-500 hover:text-gray-200 h-7 w-7 p-0"
          data-testid={`propose-btn-${param.key}`}>
          <Edit3 className="w-3 h-3" />
        </Button>
      </div>
    </div>
  );
}

/* ─── Propose Modal ─── */
function ProposeForm({ param, wallet, onClose, onCreated }) {
  const [value, setValue] = useState(param.current_value);
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!reason.trim()) { toast.error('Reason required'); return; }
    if (value === param.current_value) { toast.error('Value unchanged'); return; }
    if (value < param.min || value > param.max) { toast.error(`Must be ${param.min}–${param.max}`); return; }
    setSubmitting(true);
    try {
      const res = await fetch(`${API}/api/governance/params/propose`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key: param.key, new_value: value, reason, proposer_wallet: wallet }),
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success('Proposal created!');
      onCreated();
      onClose();
    } catch (e) { toast.error(e.message); }
    setSubmitting(false);
  };

  return (
    <Card className="bg-white/[0.03] border-[#F7931A]/15 mb-4" data-testid="propose-form">
      <CardContent className="p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="text-sm font-semibold heading-font text-gray-200">Propose Change: {param.label}</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-300"><XCircle className="w-4 h-4" /></button>
        </div>
        <div className="grid grid-cols-3 gap-3 text-center text-[10px]">
          <div className="bg-white/[0.03] rounded-lg p-2">
            <span className="text-gray-600 block">Current</span>
            <span className="text-gray-200 font-bold mono">{param.current_value}</span>
          </div>
          <div className="bg-white/[0.03] rounded-lg p-2">
            <span className="text-gray-600 block">Min</span>
            <span className="text-gray-400 mono">{param.min}</span>
          </div>
          <div className="bg-white/[0.03] rounded-lg p-2">
            <span className="text-gray-600 block">Max</span>
            <span className="text-gray-400 mono">{param.max}</span>
          </div>
        </div>
        <div>
          <label className="text-[10px] text-gray-500 mb-1 block">New Value</label>
          <Input type="number" value={value} onChange={e => setValue(parseFloat(e.target.value) || 0)}
            min={param.min} max={param.max} step={param.type === 'float' ? 0.1 : 1}
            className="bg-white/[0.03] border-white/10 text-gray-100 text-sm mono" data-testid="param-value-input" />
        </div>
        <div>
          <label className="text-[10px] text-gray-500 mb-1 block">Reason</label>
          <Input value={reason} onChange={e => setReason(e.target.value)} placeholder="Why should this change?"
            className="bg-white/[0.03] border-white/10 text-gray-100 text-sm" data-testid="param-reason-input" />
        </div>
        <Button onClick={handleSubmit} disabled={submitting} className="w-full bg-[#F7931A] text-black hover:bg-[#F7931A]/90 text-xs" data-testid="submit-proposal-btn">
          {submitting ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : <Vote className="w-3 h-3 mr-1" />}
          Create Proposal
        </Button>
      </CardContent>
    </Card>
  );
}

/* ─── Proposal Card ─── */
function ProposalCard({ proposal, wallet, onVote, onExecute }) {
  const statusColor = STATUS_COLORS[proposal.status] || STATUS_COLORS.active;
  const catColor = CATEGORY_COLORS[proposal.category] || CATEGORY_COLORS.gamification;
  const hasVoted = proposal.voters?.includes(wallet?.toLowerCase());
  const canExecute = proposal.status === 'active' && proposal.votes_for > proposal.votes_against;
  const totalVotes = proposal.votes_for + proposal.votes_against + proposal.votes_abstain;

  return (
    <Card className="bg-white/[0.03] border-white/5" data-testid={`proposal-${proposal.proposal_id}`}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div>
            <div className="flex items-center gap-2 mb-1">
              <Badge variant="outline" className={`text-[9px] ${catColor}`}>{proposal.category}</Badge>
              <Badge variant="outline" className={`text-[9px] ${statusColor}`}>{proposal.status}</Badge>
              <span className="text-[9px] text-gray-600">#{proposal.proposal_id}</span>
            </div>
            <p className="text-xs font-medium text-gray-200">{proposal.label}: {proposal.current_value} → <span className="text-[#F7931A] font-bold">{proposal.proposed_value}</span></p>
          </div>
          <span className="text-[9px] text-gray-600">{timeAgo(proposal.created_at)}</span>
        </div>
        <p className="text-[10px] text-gray-500 mb-3 italic">"{proposal.reason}"</p>

        {/* Vote bars */}
        <div className="mb-3">
          <div className="flex items-center justify-between text-[9px] text-gray-500 mb-1">
            <span>Votes ({totalVotes})</span>
            <span>{proposal.votes_for} FOR / {proposal.votes_against} AGAINST / {proposal.votes_abstain} ABSTAIN</span>
          </div>
          <div className="flex h-2 rounded-full overflow-hidden bg-white/5">
            {totalVotes > 0 && (
              <>
                <div className="bg-emerald-500 h-full" style={{ width: `${(proposal.votes_for / totalVotes) * 100}%` }} />
                <div className="bg-red-500 h-full" style={{ width: `${(proposal.votes_against / totalVotes) * 100}%` }} />
                <div className="bg-gray-500 h-full" style={{ width: `${(proposal.votes_abstain / totalVotes) * 100}%` }} />
              </>
            )}
          </div>
        </div>

        {/* Actions */}
        {proposal.status === 'active' && (
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" disabled={hasVoted} onClick={() => onVote(proposal.proposal_id, 1)}
              className="flex-1 h-7 text-[10px] border-emerald-500/20 text-emerald-400 hover:bg-emerald-500/10" data-testid={`vote-for-${proposal.proposal_id}`}>
              <ThumbsUp className="w-3 h-3 mr-1" />For
            </Button>
            <Button size="sm" variant="outline" disabled={hasVoted} onClick={() => onVote(proposal.proposal_id, 0)}
              className="flex-1 h-7 text-[10px] border-red-500/20 text-red-400 hover:bg-red-500/10" data-testid={`vote-against-${proposal.proposal_id}`}>
              <ThumbsDown className="w-3 h-3 mr-1" />Against
            </Button>
            <Button size="sm" variant="outline" disabled={hasVoted} onClick={() => onVote(proposal.proposal_id, 2)}
              className="flex-1 h-7 text-[10px] border-gray-500/20 text-gray-400 hover:bg-gray-500/10" data-testid={`vote-abstain-${proposal.proposal_id}`}>
              <Minus className="w-3 h-3 mr-1" />Abstain
            </Button>
            {canExecute && (
              <Button size="sm" onClick={() => onExecute(proposal.proposal_id)}
                className="h-7 text-[10px] bg-[#F7931A] text-black" data-testid={`execute-${proposal.proposal_id}`}>
                <Play className="w-3 h-3 mr-1" />Execute
              </Button>
            )}
          </div>
        )}
        {proposal.status === 'executed' && (
          <div className="flex items-center gap-1.5 text-[10px] text-emerald-400">
            <CheckCircle2 className="w-3 h-3" />Executed {timeAgo(proposal.executed_at)}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/* ─── MAIN PAGE ─── */
export default function GovernanceParamsPage({ embedded = false }) {
  const [tab, setTab] = useState('parameters');
  const [params, setParams] = useState({});
  const [categories, setCategories] = useState({});
  const [proposals, setProposals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [proposing, setProposing] = useState(null);
  const [wallet] = useState('0x92537BE4d02aa426F0BAB04F6300e9124f1B4Ac8');

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [paramsRes, proposalsRes] = await Promise.all([
        fetch(`${API}/api/governance/params/all`),
        fetch(`${API}/api/governance/params/proposals/list`),
      ]);
      const [p, pr] = await Promise.all([paramsRes.json(), proposalsRes.json()]);
      setParams(p.parameters || {});
      setCategories(p.categories || {});
      setProposals(pr.proposals || []);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleVote = async (proposalId, voteType) => {
    try {
      const res = await fetch(`${API}/api/governance/params/proposals/${proposalId}/vote`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ voter_wallet: wallet, vote_type: voteType }),
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success('Vote cast!');
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  const handleExecute = async (proposalId) => {
    try {
      const res = await fetch(`${API}/api/governance/params/proposals/${proposalId}/execute`, {
        method: 'POST',
      });
      if (!res.ok) throw new Error((await res.json()).detail);
      toast.success('Proposal executed! Parameter updated.');
      loadData();
    } catch (e) { toast.error(e.message); }
  };

  // Group params by category
  const grouped = {};
  Object.values(params).forEach(p => {
    if (!grouped[p.category]) grouped[p.category] = [];
    grouped[p.category].push(p);
  });

  if (embedded) {
    return (
      <div className="text-gray-100 px-4 lg:px-6 py-6" data-testid="governance-params-page">
        <div className="max-w-5xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl sm:text-3xl heading-font font-bold tracking-tight" data-testid="params-heading">
              DAO-Governed Parameters
            </h1>
            <p className="text-gray-400 text-sm mt-1">
              These values control the Metaverse Economy. Token holders propose and vote to change them.
            </p>
          </div>
          <Button size="sm" variant="outline" onClick={loadData} className="border-white/10 text-gray-400" data-testid="refresh-params-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="params-stats">
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
            <p className="text-lg font-bold heading-font mono text-[#F7931A]">{Object.keys(params).length}</p>
            <p className="text-[9px] text-gray-500">Parameters</p>
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
            <p className="text-lg font-bold heading-font mono text-gray-100">{proposals.length}</p>
            <p className="text-[9px] text-gray-500">Proposals</p>
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
            <p className="text-lg font-bold heading-font mono text-emerald-400">
              {Object.values(params).filter(p => !p.is_default).length}
            </p>
            <p className="text-[9px] text-gray-500">DAO Overrides</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList className="bg-white/[0.03] border border-white/5" data-testid="params-tabs">
            <TabsTrigger value="parameters" className="text-xs gap-1" data-testid="tab-parameters">
              <Settings className="w-3 h-3" />Parameters
            </TabsTrigger>
            <TabsTrigger value="proposals" className="text-xs gap-1" data-testid="tab-proposals">
              <Vote className="w-3 h-3" />Proposals ({proposals.filter(p => p.status === 'active').length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="parameters">
            {proposing && (
              <ProposeForm param={proposing} wallet={wallet} onClose={() => setProposing(null)} onCreated={loadData} />
            )}
            {loading ? (
              <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#F7931A]" /></div>
            ) : (
              <div className="space-y-6" data-testid="params-categories">
                {Object.entries(grouped).map(([catKey, catParams]) => (
                  <div key={catKey}>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="outline" className={`text-[9px] ${CATEGORY_COLORS[catKey]}`}>
                        {categories[catKey]?.label || catKey}
                      </Badge>
                      <span className="text-[10px] text-gray-600">{categories[catKey]?.description}</span>
                    </div>
                    <div className="space-y-2">
                      {catParams.map(p => (
                        <ParamCard key={p.key} param={p} onPropose={setProposing} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="proposals">
            {proposals.length === 0 ? (
              <p className="text-gray-600 text-center py-12 text-sm">No proposals yet. Hover over a parameter and click the edit icon to propose a change.</p>
            ) : (
              <div className="space-y-3" data-testid="proposals-list">
                {proposals.map(p => (
                  <ProposalCard key={p.proposal_id} proposal={p} wallet={wallet} onVote={handleVote} onExecute={handleExecute} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0a0e1a] text-gray-100" data-testid="governance-params-page">
      {/* Nav */}
      <nav className="sticky top-0 z-50 border-b border-white/5 bg-[#0a0e1a]/80 backdrop-blur-xl">
        <div className="max-w-5xl mx-auto px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/governance" className="text-gray-400 hover:text-white transition-colors" data-testid="back-to-governance">
              <ArrowLeft className="w-5 h-5" />
            </Link>
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-[#F7931A] to-[#B08D57] flex items-center justify-center">
                <Settings className="w-4 h-4 text-white" />
              </div>
              <span className="heading-font text-base font-semibold tracking-tight">Protocol Parameters</span>
            </div>
          </div>
          <Button size="sm" variant="outline" onClick={loadData} className="border-white/10 text-gray-400" data-testid="refresh-params-btn">
            <RefreshCw className="w-3.5 h-3.5 mr-1.5" />Refresh
          </Button>
        </div>
      </nav>

      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="mb-6">
          <h1 className="text-2xl sm:text-3xl heading-font font-bold tracking-tight mb-2" data-testid="params-heading">
            DAO-Governed Parameters
          </h1>
          <p className="text-gray-400 text-sm">
            These values control the Metaverse Economy. Token holders propose and vote to change them.
          </p>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-3 gap-3 mb-6" data-testid="params-stats">
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
            <p className="text-lg font-bold heading-font mono text-[#F7931A]">{Object.keys(params).length}</p>
            <p className="text-[9px] text-gray-500">Parameters</p>
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
            <p className="text-lg font-bold heading-font mono text-gray-100">{proposals.length}</p>
            <p className="text-[9px] text-gray-500">Proposals</p>
          </div>
          <div className="bg-white/[0.03] border border-white/5 rounded-xl p-3 text-center">
            <p className="text-lg font-bold heading-font mono text-emerald-400">
              {Object.values(params).filter(p => !p.is_default).length}
            </p>
            <p className="text-[9px] text-gray-500">DAO Overrides</p>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="space-y-6">
          <TabsList className="bg-white/[0.03] border border-white/5" data-testid="params-tabs">
            <TabsTrigger value="parameters" className="text-xs gap-1" data-testid="tab-parameters">
              <Settings className="w-3 h-3" />Parameters
            </TabsTrigger>
            <TabsTrigger value="proposals" className="text-xs gap-1" data-testid="tab-proposals">
              <Vote className="w-3 h-3" />Proposals ({proposals.filter(p => p.status === 'active').length})
            </TabsTrigger>
          </TabsList>

          {/* Parameters Tab */}
          <TabsContent value="parameters">
            {proposing && (
              <ProposeForm param={proposing} wallet={wallet} onClose={() => setProposing(null)} onCreated={loadData} />
            )}
            {loading ? (
              <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-[#F7931A]" /></div>
            ) : (
              <div className="space-y-6" data-testid="params-categories">
                {Object.entries(grouped).map(([catKey, catParams]) => (
                  <div key={catKey}>
                    <div className="flex items-center gap-2 mb-3">
                      <Badge variant="outline" className={`text-[9px] ${CATEGORY_COLORS[catKey]}`}>
                        {categories[catKey]?.label || catKey}
                      </Badge>
                      <span className="text-[10px] text-gray-600">{categories[catKey]?.description}</span>
                    </div>
                    <div className="space-y-2">
                      {catParams.map(p => (
                        <ParamCard key={p.key} param={p} onPropose={setProposing} />
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Proposals Tab */}
          <TabsContent value="proposals">
            {proposals.length === 0 ? (
              <p className="text-gray-600 text-center py-12 text-sm">No proposals yet. Hover over a parameter and click the edit icon to propose a change.</p>
            ) : (
              <div className="space-y-3" data-testid="proposals-list">
                {proposals.map(p => (
                  <ProposalCard key={p.proposal_id} proposal={p} wallet={wallet} onVote={handleVote} onExecute={handleExecute} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
